import React, { Component, ErrorInfo } from "react";
import * as Sentry from "@sentry/react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** If true, shows a simpler inline fallback instead of a full-screen overlay */
  inline?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches rendering errors in child components and shows a recovery UI
 * instead of a black/white screen. Reports errors to Sentry automatically.
 *
 * Usage:
 *   <ErrorBoundary>           — full-screen fallback (for root wrapper)
 *   <ErrorBoundary inline>    — inline fallback (for route-level wrappers)
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary] Caught error:", error, errorInfo);
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo.componentStack || "" },
      },
    });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
  };

  handleFullReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.inline) {
      return (
        <div style={styles.inlineContainer}>
          <p style={styles.inlineText}>Something went wrong on this page.</p>
          <button style={styles.button} onClick={this.handleReload}>
            Try Again
          </button>
        </div>
      );
    }

    return (
      <div style={styles.fullScreenContainer}>
        <div style={styles.content}>
          <h1 style={styles.title}>WerkApp</h1>
          <p style={styles.message}>Something went wrong.</p>
          <p style={styles.submessage}>
            The error has been reported and we're looking into it.
          </p>
          <button style={styles.button} onClick={this.handleReload}>
            Try Again
          </button>
          <button style={styles.linkButton} onClick={this.handleFullReload}>
            Restart App
          </button>
        </div>
      </div>
    );
  }
}

// Inline styles so the fallback UI renders even if CSS/Ionic fails to load.
const styles: Record<string, React.CSSProperties> = {
  fullScreenContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    width: "100vw",
    backgroundColor: "#1a1a2e",
    color: "#ffffff",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    padding: 24,
    boxSizing: "border-box",
  },
  content: {
    textAlign: "center" as const,
    maxWidth: 400,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 16,
  },
  message: {
    fontSize: 18,
    marginBottom: 8,
  },
  submessage: {
    fontSize: 14,
    opacity: 0.7,
    marginBottom: 32,
  },
  button: {
    display: "block",
    width: "100%",
    padding: "14px 24px",
    fontSize: 16,
    fontWeight: 600,
    color: "#ffffff",
    backgroundColor: "#4a6cf7",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    marginBottom: 12,
  },
  linkButton: {
    display: "block",
    width: "100%",
    padding: "10px 24px",
    fontSize: 14,
    color: "#8888aa",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    textDecoration: "underline",
  },
  inlineContainer: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
    textAlign: "center" as const,
  },
  inlineText: {
    fontSize: 16,
    marginBottom: 16,
    color: "#666",
  },
};

export default ErrorBoundary;
